import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useRef } from "react";

interface UrlAnswerFieldProps {
  value: unknown;
  onChange: (value: string | string[]) => void;
  onBlur?: () => void;
  allowMultiple?: boolean;
  error?: string | null;
}

function rowsFromValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    const rows = value.map((item) => (typeof item === "string" ? item : ""));
    return rows.length > 0 ? rows : [""];
  }
  if (typeof value === "string" && value.trim()) return [value];
  return [""];
}

/** One URL input, or a repeatable list when the question allows multiple. */
export function UrlAnswerField({
  value,
  onChange,
  onBlur,
  allowMultiple = false,
  error,
}: UrlAnswerFieldProps) {
  const idsRef = useRef<string[]>([]);
  if (!allowMultiple) {
    const text =
      typeof value === "string"
        ? value
        : Array.isArray(value) && typeof value[0] === "string"
          ? value[0]
          : "";
    return (
      <div className="space-y-1">
        <Input
          type="url"
          inputMode="url"
          placeholder="https://"
          value={text}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          aria-invalid={Boolean(error)}
        />
        {error ? <p className="text-destructive text-sm">{error}</p> : null}
      </div>
    );
  }

  const rows = rowsFromValue(value);
  const ids = idsRef.current.slice(0, rows.length);
  while (ids.length < rows.length) {
    ids.push(crypto.randomUUID());
  }
  idsRef.current = ids;
  const items = rows.map((row, index) => ({
    id: ids[index] ?? crypto.randomUUID(),
    row,
    index,
  }));
  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div key={item.id} className="flex items-center gap-2">
          <Input
            type="url"
            inputMode="url"
            placeholder="https://"
            value={item.row}
            onChange={(event) => {
              const next = [...rows];
              next[item.index] = event.target.value;
              onChange(next);
            }}
            onBlur={onBlur}
            aria-invalid={Boolean(error)}
          />
          {rows.length > 1 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const next = rows.filter(
                  (_, rowIndex) => rowIndex !== item.index,
                );
                idsRef.current = ids.filter(
                  (_, rowIndex) => rowIndex !== item.index,
                );
                onChange(next.length > 0 ? next : [""]);
              }}
            >
              Remove
            </Button>
          ) : null}
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...rows, ""])}
      >
        Add URL
      </Button>
      {error ? <p className="text-destructive text-sm">{error}</p> : null}
    </div>
  );
}
